import type { BattlefieldRow, CardDefinition, FormatId, ImportedDeckPayload, TokenTemplate } from './types.ts';
import { GAME_CHANGER_NAMES, lookupSeedCard } from './cardCatalog.ts';

export { GAME_CHANGER_NAMES };

export function classifyBattlefieldRow(typeLine: string): BattlefieldRow {
  const lower = typeLine.toLowerCase();
  if (lower.includes('land') && !lower.includes('creature')) return 'lands';
  if (lower.includes('creature')) return 'creatures';
  return 'other';
}

function scryfallImg(name: string): string {
  return `https://api.scryfall.com/cards/named?format=image&version=normal&exact=${encodeURIComponent(name)}`;
}

const KNOWN_CARDS: Record<string, CardDefinition> = {
  'Atraxa, Praetors\' Voice': {
    scryfallId: 'd0d33d52-3d28-4635-b985-51e126289259',
    name: "Atraxa, Praetors' Voice",
    manaCost: '{G}{W}{U}{B}',
    cmc: 4,
    typeLine: 'Legendary Creature — Phyrexian Angel Horror',
    oracleText: 'Flying, vigilance, deathtouch, lifelink\nAt the beginning of your end step, proliferate.',
    colors: ['W', 'U', 'B', 'G'],
    power: '4',
    toughness: '4',
    imageUrl: scryfallImg("Atraxa, Praetors' Voice"),
    layout: 'normal',
  },
  'Krenko, Mob Boss': {
    scryfallId: 'cd9fec9d-23c8-4d35-97c1-9499527198fb',
    name: 'Krenko, Mob Boss',
    manaCost: '{2}{R}{R}',
    cmc: 4,
    typeLine: 'Legendary Creature — Goblin Warrior',
    oracleText: '{T}: Create X 1/1 red Goblin creature tokens, where X is the number of Goblins you control.',
    colors: ['R'],
    power: '3',
    toughness: '3',
    imageUrl: scryfallImg('Krenko, Mob Boss'),
    layout: 'normal',
    relatedTokens: [
      {
        name: 'Goblin',
        typeLine: 'Token Creature — Goblin',
        power: '1',
        toughness: '1',
        colors: ['R'],
      },
    ],
  },
  'Sol Ring': {
    scryfallId: '4cbc6901-6a4a-4d0a-83ea-7eefa3b35021',
    name: 'Sol Ring',
    manaCost: '{1}',
    cmc: 1,
    typeLine: 'Artifact',
    oracleText: '{T}: Add {C}{C}.',
    colors: [],
    imageUrl: scryfallImg('Sol Ring'),
    layout: 'normal',
  },
  'Arcane Signet': {
    scryfallId: '1bf50ef8-e7e8-4099-ba91-0d7c31b04541',
    name: 'Arcane Signet',
    manaCost: '{2}',
    cmc: 2,
    typeLine: 'Artifact',
    oracleText: "{T}: Add one mana of any color in your commander's color identity.",
    colors: [],
    imageUrl: scryfallImg('Arcane Signet'),
    layout: 'normal',
  },
  'Swords to Plowshares': {
    scryfallId: '9be2bf8f-5e49-4345-81dc-08eeb7fa4fee',
    name: 'Swords to Plowshares',
    manaCost: '{W}',
    cmc: 1,
    typeLine: 'Instant',
    oracleText: 'Exile target creature. Its controller gains life equal to its power.',
    colors: ['W'],
    imageUrl: scryfallImg('Swords to Plowshares'),
    layout: 'normal',
  },
  'Counterspell': {
    scryfallId: '8493131c-0a7b-4be6-a8a2-0b425f4f67fb',
    name: 'Counterspell',
    manaCost: '{U}{U}',
    cmc: 2,
    typeLine: 'Instant',
    oracleText: 'Counter target spell.',
    colors: ['U'],
    imageUrl: scryfallImg('Counterspell'),
    layout: 'normal',
  },
  'Rhystic Study': {
    scryfallId: 'd6914dba-0d27-4055-ac34-b3ebf5802221',
    name: 'Rhystic Study',
    manaCost: '{2}{U}',
    cmc: 3,
    typeLine: 'Enchantment',
    oracleText: 'Whenever an opponent casts a spell, you may draw a card unless that player pays {1}.',
    colors: ['U'],
    imageUrl: scryfallImg('Rhystic Study'),
    layout: 'normal',
    isGameChanger: true,
  },
  'Smothering Tithe': {
    scryfallId: '861b5889-0183-4bee-afeb-a4b2aa700a8e',
    name: 'Smothering Tithe',
    manaCost: '{3}{W}',
    cmc: 4,
    typeLine: 'Enchantment',
    oracleText: 'Whenever an opponent draws a card, that player may pay {2}. If the player doesn\'t, you create a Treasure token.',
    colors: ['W'],
    imageUrl: scryfallImg('Smothering Tithe'),
    layout: 'normal',
    isGameChanger: true,
    relatedTokens: [
      {
        name: 'Treasure',
        typeLine: 'Token Artifact — Treasure',
        colors: [],
        oracleText: '{T}, Sacrifice this artifact: Add one mana of any color.',
      },
    ],
  },
  'Delver of Secrets // Insectile Aberration': {
    scryfallId: 'abff6c81-65a4-48fa-ba8f-580f87b0344a',
    name: 'Delver of Secrets',
    manaCost: '{U}',
    cmc: 1,
    typeLine: 'Creature — Human Wizard',
    oracleText: 'At the beginning of your upkeep, look at the top card of your library. You may reveal that card. If an instant or sorcery card is revealed this way, transform Delver of Secrets.',
    colors: ['U'],
    power: '1',
    toughness: '1',
    imageUrl: scryfallImg('Delver of Secrets'),
    backName: 'Insectile Aberration',
    backTypeLine: 'Creature — Human Insect',
    backOracleText: 'Flying (3/2)',
    backImageUrl: `https://api.scryfall.com/cards/named?format=image&version=normal&face=back&exact=${encodeURIComponent('Delver of Secrets')}`,
    layout: 'transform',
  },
  'Birds of Paradise': {
    scryfallId: 'feefe9f0-24a6-461c-9ef1-86c5a6f33b83',
    name: 'Birds of Paradise',
    manaCost: '{G}',
    cmc: 1,
    typeLine: 'Creature — Bird',
    oracleText: 'Flying\n{T}: Add one mana of any color.',
    colors: ['G'],
    power: '0',
    toughness: '1',
    imageUrl: scryfallImg('Birds of Paradise'),
    layout: 'normal',
  },
  'Cultivate': {
    scryfallId: 'e19cd13b-252c-4410-9785-1a8e0e0c5503',
    name: 'Cultivate',
    manaCost: '{2}{G}',
    cmc: 3,
    typeLine: 'Sorcery',
    oracleText: 'Search your library for up to two basic land cards, reveal those cards, put one onto the battlefield tapped and the other into your hand, then shuffle.',
    colors: ['G'],
    imageUrl: scryfallImg('Cultivate'),
    layout: 'normal',
  },
  'Lightning Bolt': {
    scryfallId: 'f29ba16f-c8fb-42fe-aabf-87089cb214a7',
    name: 'Lightning Bolt',
    manaCost: '{R}',
    cmc: 1,
    typeLine: 'Instant',
    oracleText: 'Lightning Bolt deals 3 damage to any target.',
    colors: ['R'],
    imageUrl: scryfallImg('Lightning Bolt'),
    layout: 'normal',
  },
  'Goblin Chieftain': {
    scryfallId: '297b01bc-e95b-4604-82ff-9d6c80f87b47',
    name: 'Goblin Chieftain',
    manaCost: '{1}{R}{R}',
    cmc: 3,
    typeLine: 'Creature — Goblin',
    oracleText: 'Haste\nOther Goblin creatures you control get +1/+1 and have haste.',
    colors: ['R'],
    power: '2',
    toughness: '2',
    imageUrl: scryfallImg('Goblin Chieftain'),
    layout: 'normal',
  },
  'Siege-Gang Commander': {
    scryfallId: '5dd6a913-88df-4fbc-964e-a8fec3ad989f',
    name: 'Siege-Gang Commander',
    manaCost: '{3}{R}{R}',
    cmc: 5,
    typeLine: 'Creature — Goblin',
    oracleText: 'When Siege-Gang Commander enters, create three 1/1 red Goblin creature tokens.\n{1}{R}, Sacrifice a Goblin: Deal 2 damage to any target.',
    colors: ['R'],
    power: '2',
    toughness: '2',
    imageUrl: scryfallImg('Siege-Gang Commander'),
    layout: 'normal',
    relatedTokens: [
      {
        name: 'Goblin',
        typeLine: 'Token Creature — Goblin',
        power: '1',
        toughness: '1',
        colors: ['R'],
      },
    ],
  },
  'Command Tower': {
    scryfallId: '14019383-0327-404b-ab4d-c6bc7fa8c5bc',
    name: 'Command Tower',
    manaCost: '',
    cmc: 0,
    typeLine: 'Land',
    oracleText: "{T}: Add one mana of any color in your commander's color identity.",
    colors: [],
    imageUrl: scryfallImg('Command Tower'),
    layout: 'normal',
  },
  'Plains': {
    scryfallId: 'basic-plains',
    name: 'Plains',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Plains',
    oracleText: '({T}: Add {W}.)',
    colors: [],
    imageUrl: scryfallImg('Plains'),
    layout: 'normal',
  },
  'Island': {
    scryfallId: 'basic-island',
    name: 'Island',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Island',
    oracleText: '({T}: Add {U}.)',
    colors: [],
    imageUrl: scryfallImg('Island'),
    layout: 'normal',
  },
  'Swamp': {
    scryfallId: 'basic-swamp',
    name: 'Swamp',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Swamp',
    oracleText: '({T}: Add {B}.)',
    colors: [],
    imageUrl: scryfallImg('Swamp'),
    layout: 'normal',
  },
  'Mountain': {
    scryfallId: 'basic-mountain',
    name: 'Mountain',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Mountain',
    oracleText: '({T}: Add {R}.)',
    colors: [],
    imageUrl: scryfallImg('Mountain'),
    layout: 'normal',
  },
  'Forest': {
    scryfallId: 'basic-forest',
    name: 'Forest',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Forest',
    oracleText: '({T}: Add {G}.)',
    colors: [],
    imageUrl: scryfallImg('Forest'),
    layout: 'normal',
  },
};

const KNOWN_CARD_BY_LOWER = new Map<string, CardDefinition>(
  Object.entries(KNOWN_CARDS).map(([k, v]) => [k.toLowerCase(), v])
);

export function buildCardDefinitionByName(
  rawName: string,
  opts: { setCode?: string; collectorNumber?: string; foil?: boolean; typeHint?: string } = {}
): CardDefinition {
  const cleaned = rawName.trim();
  const known =
    KNOWN_CARD_BY_LOWER.get(cleaned.toLowerCase()) ??
    KNOWN_CARD_BY_LOWER.get(cleaned.split('//')[0].trim().toLowerCase()) ??
    lookupSeedCard(cleaned);
  if (known) {
    return {
      ...known,
      setCode: opts.setCode ?? known.setCode,
      collectorNumber: opts.collectorNumber ?? known.collectorNumber,
      foil: opts.foil ?? known.foil,
    };
  }

  const isDfc = cleaned.includes('//');
  const [frontName, backName] = isDfc
    ? cleaned.split('//').map((p) => p.trim())
    : [cleaned, undefined];
  const lower = frontName.toLowerCase();
  const inferredLand =
    opts.typeHint === 'land' ||
    /\b(tower|sanctum|garden|fountain|shrine|tomb|cradle|mesa|tarn|mire|strand|delta|catacombs|heath|rainforest|foothills|flats|cavern|triome|verge|pathway|channel|boseiju|otawara|wasteland|strip mine)\b/i.test(
      frontName
    );

  return {
    scryfallId: `named:${lower.replace(/[^a-z0-9]+/g, '-')}`,
    name: frontName,
    manaCost: inferredLand ? '' : '{2}',
    cmc: inferredLand ? 0 : 2,
    typeLine: inferredLand ? 'Land' : 'Spell / Permanent',
    oracleText: '',
    colors: [],
    imageUrl: scryfallImg(frontName),
    backName,
    backImageUrl: backName
      ? `https://api.scryfall.com/cards/named?format=image&version=normal&face=back&exact=${encodeURIComponent(frontName)}`
      : undefined,
    layout: isDfc ? 'transform' : 'normal',
    setCode: opts.setCode,
    collectorNumber: opts.collectorNumber,
    foil: opts.foil,
    isGameChanger: GAME_CHANGER_NAMES.has(lower),
  };
}

export interface PlainTextParseResult {
  deck: ImportedDeckPayload;
  warnings: string[];
}

/**
 * Parses Arena, MTGO, Moxfield, and plain-text decklists:
 *   "1 Sol Ring"
 *   "1x Sol Ring (CMM) 400 *F*"
 *   "Commander" / "Sideboard" / "Maybeboard" section headers
 * Sideboard and Maybeboard are strictly separated from mainboard so they never enter the library.
 */
export function parsePlainTextDeck(
  text: string,
  deckName = 'Imported Deck',
  format: FormatId = 'commander'
): PlainTextParseResult {
  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const commanders: CardDefinition[] = [];
  const companions: CardDefinition[] = [];
  const sideboard: { card: CardDefinition; quantity: number }[] = [];
  const maybeboard: { card: CardDefinition; quantity: number }[] = [];
  const warnings: string[] = [];

  let section: 'main' | 'commander' | 'companion' | 'sideboard' | 'maybeboard' = 'main';
  const lines = text.split(/\r?\n/);

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith('//') || line.startsWith('#')) {
      if (/^\/\/\s*commander/i.test(line)) section = 'commander';
      else if (/^\/\/\s*sideboard/i.test(line)) section = 'sideboard';
      else if (/^\/\/\s*maybeboard/i.test(line)) section = 'maybeboard';
      continue;
    }

    const headerMatch = line.match(/^(deck|mainboard|commander|commanders|companion|sideboard|maybeboard|considering)\s*:?$/i);
    if (headerMatch) {
      const h = headerMatch[1].toLowerCase();
      if (h === 'deck' || h === 'mainboard') section = 'main';
      else if (h === 'commander' || h === 'commanders') section = 'commander';
      else if (h === 'companion') section = 'companion';
      else if (h === 'sideboard') section = 'sideboard';
      else section = 'maybeboard';
      continue;
    }

    if (/^SB:\s+/i.test(line)) {
      const rest = line.replace(/^SB:\s+/i, '');
      const parsed = parseCardLine(rest);
      if (parsed) sideboard.push({ card: parsed.card, quantity: parsed.qty });
      else warnings.push(`Unrecognized sideboard line: "${line}"`);
      continue;
    }

    const parsed = parseCardLine(line);
    if (!parsed) {
      warnings.push(`Could not parse line: "${line}"`);
      continue;
    }

    if (section === 'commander' || parsed.inlineTag === 'commander') {
      for (let i = 0; i < parsed.qty; i++) commanders.push(parsed.card);
      if (section === 'commander') {
        // Arena puts blank line after Commander section; single-commander lists often omit "Deck" header if blank line follows
      }
    } else if (section === 'companion') {
      companions.push(parsed.card);
    } else if (section === 'sideboard' || parsed.inlineTag === 'sideboard') {
      sideboard.push({ card: parsed.card, quantity: parsed.qty });
    } else if (section === 'maybeboard' || parsed.inlineTag === 'maybeboard') {
      maybeboard.push({ card: parsed.card, quantity: parsed.qty });
    } else {
      mainboard.push({ card: parsed.card, quantity: parsed.qty });
    }
  }

  return {
    deck: {
      name: deckName,
      format,
      mainboard,
      commanders,
      companions,
      sideboard,
      maybeboard,
    },
    warnings,
  };
}

function parseCardLine(line: string): {
  qty: number;
  card: CardDefinition;
  inlineTag?: 'commander' | 'sideboard' | 'maybeboard';
} | null {
  let working = line.trim();
  let inlineTag: 'commander' | 'sideboard' | 'maybeboard' | undefined;
  if (/\[commander(\{top\})?\]/i.test(working) || /\*CMDR\*/i.test(working)) {
    inlineTag = 'commander';
    working = working.replace(/\[commander(\{top\})?\]/gi, '').replace(/\*CMDR\*/gi, '').trim();
  } else if (/\[maybeboard\]/i.test(working)) {
    inlineTag = 'maybeboard';
    working = working.replace(/\[maybeboard\]/gi, '').trim();
  } else if (/\[sideboard\]/i.test(working)) {
    inlineTag = 'sideboard';
    working = working.replace(/\[sideboard\]/gi, '').trim();
  }

  const foil = /\*F\*|\(foil\)/i.test(working);
  working = working.replace(/\*F\*|\*E\*|\(foil\)/gi, '').trim();
  working = working.replace(/\[[^\]]+\]/g, '').trim();

  const match = working.match(/^(\d+)x?\s+(.+?)(?:\s+\(([A-Za-z0-9]{2,6})\)(?:\s+([A-Za-z0-9★-]+))?)?$/);
  if (!match) return null;

  const qty = Math.max(1, Math.min(99, Number.parseInt(match[1], 10)));
  const name = match[2].trim();
  if (!name) return null;

  return {
    qty,
    card: buildCardDefinitionByName(name, {
      setCode: match[3]?.toUpperCase(),
      collectorNumber: match[4],
      foil,
    }),
    inlineTag,
  };
}

export function extractTokensFromDeck(deck: ImportedDeckPayload): TokenTemplate[] {
  const seen = new Set<string>();
  const tokens: TokenTemplate[] = [];
  const all = [
    ...deck.commanders,
    ...deck.mainboard.map((m) => m.card),
    ...deck.sideboard.map((s) => s.card),
  ];
  for (const card of all) {
    for (const t of card.relatedTokens ?? []) {
      const key = `${t.name}|${t.power ?? ''}|${t.toughness ?? ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        tokens.push(t);
      }
    }
  }
  if (tokens.length === 0) {
    tokens.push({
      name: 'Treasure',
      typeLine: 'Token Artifact — Treasure',
      colors: [],
      oracleText: '{T}, Sacrifice this artifact: Add one mana of any color.',
    });
  }
  return tokens;
}

export const STARTER_DECKS: ImportedDeckPayload[] = [
  {
    deckId: 'starter-atraxa-superfriends',
    name: "Atraxa Proliferate (Commander)",
    format: 'commander',
    commanders: [KNOWN_CARDS["Atraxa, Praetors' Voice"]],
    mainboard: [
      { card: KNOWN_CARDS['Sol Ring'], quantity: 1 },
      { card: KNOWN_CARDS['Arcane Signet'], quantity: 1 },
      { card: KNOWN_CARDS['Swords to Plowshares'], quantity: 4 },
      { card: KNOWN_CARDS['Counterspell'], quantity: 4 },
      { card: KNOWN_CARDS['Rhystic Study'], quantity: 2 },
      { card: KNOWN_CARDS['Smothering Tithe'], quantity: 2 },
      { card: KNOWN_CARDS['Delver of Secrets // Insectile Aberration'], quantity: 4 },
      { card: KNOWN_CARDS['Birds of Paradise'], quantity: 6 },
      { card: KNOWN_CARDS['Cultivate'], quantity: 6 },
      { card: buildCardDefinitionByName('Eternal Witness'), quantity: 4 },
      { card: buildCardDefinitionByName('Evolution Sage'), quantity: 4 },
      { card: buildCardDefinitionByName('Astral Cornucopia'), quantity: 4 },
      { card: buildCardDefinitionByName('Everflowing Chalice'), quantity: 4 },
      { card: buildCardDefinitionByName('Tekuthal, Inquiry Dominus'), quantity: 3 },
      { card: buildCardDefinitionByName('Flux Channeler'), quantity: 4 },
      { card: buildCardDefinitionByName('Farewell'), quantity: 3 },
      { card: buildCardDefinitionByName('Heroic Intervention'), quantity: 4 },
      { card: buildCardDefinitionByName('Assassin\'s Trophy'), quantity: 3 },
      { card: KNOWN_CARDS['Command Tower'], quantity: 4 },
      { card: KNOWN_CARDS['Plains'], quantity: 8 },
      { card: KNOWN_CARDS['Island'], quantity: 9 },
      { card: KNOWN_CARDS['Swamp'], quantity: 7 },
      { card: KNOWN_CARDS['Forest'], quantity: 8 },
    ],
    sideboard: [{ card: buildCardDefinitionByName('mystic remora'), quantity: 1 }],
    maybeboard: [{ card: buildCardDefinitionByName('doubling season'), quantity: 1 }],
  },
  {
    deckId: 'starter-krenko-goblins',
    name: 'Krenko Goblin Swarm (Commander)',
    format: 'commander',
    commanders: [KNOWN_CARDS['Krenko, Mob Boss']],
    mainboard: [
      { card: KNOWN_CARDS['Sol Ring'], quantity: 1 },
      { card: KNOWN_CARDS['Arcane Signet'], quantity: 1 },
      { card: KNOWN_CARDS['Lightning Bolt'], quantity: 8 },
      { card: KNOWN_CARDS['Goblin Chieftain'], quantity: 8 },
      { card: KNOWN_CARDS['Siege-Gang Commander'], quantity: 6 },
      { card: buildCardDefinitionByName('Goblin Warchief'), quantity: 6 },
      { card: buildCardDefinitionByName('Skirk Prospector'), quantity: 6 },
      { card: buildCardDefinitionByName('Goblin Matron'), quantity: 6 },
      { card: buildCardDefinitionByName('Impact Tremors'), quantity: 6 },
      { card: buildCardDefinitionByName('Chaos Warp'), quantity: 5 },
      { card: buildCardDefinitionByName('Blasphemous Act'), quantity: 4 },
      { card: buildCardDefinitionByName('Skullclamp'), quantity: 4 },
      { card: KNOWN_CARDS['Mountain'], quantity: 38 },
    ],
    sideboard: [],
    maybeboard: [],
  },
  {
    deckId: 'starter-delver-tempo',
    name: 'Izzet Delver Tempo (60-Card)',
    format: 'modern',
    commanders: [],
    mainboard: [
      { card: KNOWN_CARDS['Delver of Secrets // Insectile Aberration'], quantity: 4 },
      { card: KNOWN_CARDS['Lightning Bolt'], quantity: 4 },
      { card: KNOWN_CARDS['Counterspell'], quantity: 4 },
      { card: buildCardDefinitionByName('Dragon\'s Rage Channeler'), quantity: 4 },
      { card: buildCardDefinitionByName('Murktide Regent'), quantity: 4 },
      { card: buildCardDefinitionByName('Expressive Iteration'), quantity: 4 },
      { card: buildCardDefinitionByName('Consider'), quantity: 4 },
      { card: buildCardDefinitionByName('Preordain'), quantity: 4 },
      { card: buildCardDefinitionByName('Unholy Heat'), quantity: 4 },
      { card: buildCardDefinitionByName('Spell Pierce'), quantity: 4 },
      { card: KNOWN_CARDS['Island'], quantity: 11 },
      { card: KNOWN_CARDS['Mountain'], quantity: 9 },
    ],
    sideboard: [
      { card: buildCardDefinitionByName('Blood Moon'), quantity: 2 },
      { card: buildCardDefinitionByName('Flusterstorm'), quantity: 2 },
    ],
    maybeboard: [],
  },
];
